import clsx from "clsx";
import React, { useEffect, useRef } from "react";

export function DualScrollbarContainer({
  children,
  className,
  fillHeight,
}: {
  children: React.ReactNode;
  className?: string;
  /**
   * Flex into the parent (which must be a flex column) and scroll the
   * content vertically inside.
   */
  fillHeight?: boolean;
}) {
  const topScrollerRef = useRef<HTMLDivElement>(null);
  const bottomScrollerRef = useRef<HTMLDivElement>(null);
  const scrollSpacerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const top = topScrollerRef.current;
    const bottom = bottomScrollerRef.current;
    const spacer = scrollSpacerRef.current;

    if (!top || !bottom || !spacer) return;

    const updateSpacerWidth = () => {
      spacer.style.width = `${bottom.scrollWidth}px`;
    };

    updateSpacerWidth();

    let resizeAnimationFrame: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeAnimationFrame !== null) {
        cancelAnimationFrame(resizeAnimationFrame);
      }
      resizeAnimationFrame = requestAnimationFrame(() => {
        updateSpacerWidth();
        resizeAnimationFrame = null;
      });
    });
    resizeObserver.observe(bottom);

    const innerContent = bottom.firstElementChild;
    if (innerContent) {
      resizeObserver.observe(innerContent);
    }

    let isTopScrolling = false;
    let isBottomScrolling = false;

    const topScrollHandler = () => {
      if (!isBottomScrolling) {
        isTopScrolling = true;
        bottom.scrollLeft = top.scrollLeft;
        requestAnimationFrame(() => {
          isTopScrolling = false;
        });
      }
    };

    const bottomScrollHandler = () => {
      if (!isTopScrolling) {
        isBottomScrolling = true;
        top.scrollLeft = bottom.scrollLeft;
        requestAnimationFrame(() => {
          isBottomScrolling = false;
        });
      }
    };

    top.addEventListener("scroll", topScrollHandler);
    bottom.addEventListener("scroll", bottomScrollHandler);

    return () => {
      top.removeEventListener("scroll", topScrollHandler);
      bottom.removeEventListener("scroll", bottomScrollHandler);
      resizeObserver.disconnect();
      if (resizeAnimationFrame !== null) {
        cancelAnimationFrame(resizeAnimationFrame);
      }
    };
  }, []);

  return (
    <div
      className={clsx(className, fillHeight && "flex flex-col flex-1 min-h-0")}
    >
      <div
        ref={topScrollerRef}
        className="overflow-x-auto overflow-y-hidden mb-1 shrink-0"
        style={{ height: "17px" }}
      >
        <div ref={scrollSpacerRef} style={{ height: "1px" }} />
      </div>

      {/* `relative` makes this scroller the containing block for absolutely
          positioned descendants (e.g. Radix checkboxes' hidden bubble inputs),
          so they scroll and clip with the content instead of overflowing the
          page through this statically-positioned container. */}
      <div
        ref={bottomScrollerRef}
        className={clsx(
          "relative overflow-x-auto w-full",
          fillHeight && "overflow-y-auto flex-1 min-h-0",
        )}
      >
        {children}
      </div>
    </div>
  );
}
